import { useCallback, useEffect } from "react";
import { type NavigateFunction, useLocation, useNavigate } from "react-router";
import queryString from "query-string";

import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import { organisationActions } from "@vega-api/custom/stores/organisations.store";
import { getCurrentPortalType } from "@vega-api/extended/auth/redirectUtils";
import { useGetRedirectUrl } from "@vega-api/generated/idp-v1/idp-v1";
import { type ManageableLimitedPartnersResponse } from "@vega-api/generated/types/manageableLimitedPartnersResponse";
import { type ProfileResponse } from "@vega-api/generated/types/profileResponse";
import {
  useGetManageableLimitedPartners,
  useGetProfile,
} from "@vega-api/generated/user-management-v1/user-management-v1";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { useToast } from "@vega-core/components/Toast/useToast";
import { isLocal } from "@vega-core/utils/global";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { CommonPaths } from "../../paths";

const LOGIN_PATH = CommonPaths.Login;

export const DEFAULT_REDIRECT_PATH = CommonPaths.Home;

export const REDIRECT_STORAGE_KEY = "vega_redirect_url";

const isProfileValid = (
  profile?: ProfileResponse,
  isProfileError?: boolean,
) => {
  if (isProfileError || !profile) {
    console.error("Profile error or profile not found.");

    return false;
  }

  return true;
};

const handlePartnerFlow = ({
  managableLimitedPartners,
  isPartnersError,
  limitedPartnerId,
  deepLink,
  navigate,
  navigateToOktaLogin,
}: {
  managableLimitedPartners?: ManageableLimitedPartnersResponse[];
  isPartnersError: boolean;
  limitedPartnerId?: string;
  deepLink?: string;
  navigate: NavigateFunction;
  navigateToOktaLogin: () => void;
}) => {
  if (isPartnersError) {
    console.error(
      "Failed to load manageable limited partners. Redirecting to login.",
    );
    navigateToOktaLogin();

    return;
  }

  if (!managableLimitedPartners || managableLimitedPartners.length === 0) {
    console.warn(
      "No manageable limited partners found for user. Redirecting to login.",
    );
    navigateToOktaLogin();

    return;
  }

  organisationActions.setOrganisationList(managableLimitedPartners);

  const limitedPartner = managableLimitedPartners.find(
    (partner) => partner.id === limitedPartnerId,
  );

  if (!limitedPartner) {
    console.warn(
      `Limited partner ID '${limitedPartnerId}' not found in list. Redirecting to login.`,
    );
    navigateToOktaLogin();

    return;
  }

  try {
    organisationActions.setOrganisation(limitedPartner);
    const targetPath = String(deepLink || DEFAULT_REDIRECT_PATH);
    console.log(`Set org ${limitedPartner.id}. Navigating to ${targetPath}`);
    navigate(targetPath, { replace: true });
  } catch (orgSetError) {
    console.error("Error setting organisation.", orgSetError);
    navigateToOktaLogin();
  }
};

const handleDirectNavigation = (
  deepLink: string,
  navigate: NavigateFunction,
) => {
  const targetPath = String(deepLink || DEFAULT_REDIRECT_PATH);
  console.log(
    `No limitedPartnerId provided. Navigating directly to ${targetPath}`,
  );

  navigate(targetPath, { replace: true });
};

export const getOKTARedirectUrl = (url?: string) => {
  if (!url) return;

  if (isLocal()) {
    const urlObj = new URL(url);
    urlObj.searchParams.set(
      "redirect_uri",
      `${window.location.origin}/login/by-auth-code`,
    );

    return urlObj.toString();
  }

  return url;
};
export const QueryRedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useToast();
  const {
    data: oktaLoginUrl,
    isError,
    isLoading,
  } = useGetRedirectUrl(
    { source: getCurrentPortalType() },
    {
      request: {
        skipInterceptors: true, // bypass interceptors
      },
    },
  );

  if (isError) {
    showToast({
      title: "Error",
      description: "Failed to redirect to Okta login.",
    });
  }

  const navigateToOktaLogin = useCallback(() => {
    const redirectUrl = getOKTARedirectUrl(oktaLoginUrl?.ssoUrl);

    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      showToast({
        title: "Error",
        description: "Failed to redirect to Okta login.",
      });
      navigate(LOGIN_PATH, { replace: true });
    }
  }, [oktaLoginUrl?.ssoUrl, navigate, showToast]);
  const { deepLink = "", limitedPartnerId = "" } = queryString.parse(
    location.search,
  );
  const shouldFetchPartners = !!limitedPartnerId;

  const { user } = useSession();

  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
  } = useGetProfile({
    query: {
      enabled: isTruthy(user),
      refetchOnWindowFocus: false,
      select: (response) => response.data,
    },
    request: {
      skipInterceptorsWithToken: true, // bypass interceptors with token
    },
  });

  const {
    data: managableLimitedPartners,
    isLoading: arePartnersLoading,
    isError: isPartnersError,
  } = useGetManageableLimitedPartners({
    query: {
      enabled: isTruthy(user) && !isProfileError && shouldFetchPartners,
      refetchOnWindowFocus: false,
      select: (response) => response.data,
    },
    request: {
      skipInterceptorsWithToken: true, // bypass interceptors
    },
  });

  const isProcessing =
    isProfileLoading ||
    (shouldFetchPartners && arePartnersLoading) ||
    isLoading;

  useEffect(() => {
    if (isProcessing) {
      // Preemptively persist redirect target if unauthenticated during loading
      if (!user) {
        try {
          const currentUrl = window.location.href;
          localStorage.setItem(REDIRECT_STORAGE_KEY, currentUrl);
        } catch (e) {
          console.error("Failed to preemptively set REDIRECT_STORAGE_KEY", e);
        }
      }

      return;
    }

    if (!isProfileValid(profile, isProfileError)) {
      try {
        const currentUrl = window.location.href;
        localStorage.setItem(REDIRECT_STORAGE_KEY, currentUrl);
      } catch (e) {
        console.error("Failed to set REDIRECT_STORAGE_KEY", e);
      }
      navigateToOktaLogin();

      return;
    }

    if (shouldFetchPartners) {
      handlePartnerFlow({
        managableLimitedPartners,
        isPartnersError,
        limitedPartnerId: String(limitedPartnerId),
        deepLink: String(deepLink),
        navigate,
        navigateToOktaLogin,
      });
    } else {
      handleDirectNavigation(String(deepLink), navigate);
    }
  }, [
    isProcessing,
    profile,
    isProfileError,
    shouldFetchPartners,
    managableLimitedPartners,
    isPartnersError,
    limitedPartnerId,
    deepLink,
    navigate,
    navigateToOktaLogin,
    user,
  ]);

  if (isProcessing) {
    return <LoadingSpinner />;
  }

  return null;
};
